import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

export default function WhyNormalQRFails() {
    useEffect(() => {
        window.scrollTo(0, 0)
        document.title = 'Why Normal or Online Printed QR Codes Fail | QRmor';
        const metaDesc = document.querySelector('meta[name="description"]');
        const content = 'Normal and online-printed QR codes can be photographed and cloned. See why they fail to protect brands from counterfeits, and how QRmor\'s non-clonable technology closes that gap.';
        if (metaDesc) {
            metaDesc.setAttribute('content', content);
        } else {
            const tag = document.createElement('meta');
            tag.name = 'description';
            tag.content = content;
            document.head.appendChild(tag);
        }
    }, []);

    const sectionStyle = { marginTop: 40 };
    const h2Style = { fontSize: 'clamp(22px, 3.2vw, 28px)', marginBottom: 16 };
    const introStyle = { color: 'var(--ink-soft)', fontSize: 16, lineHeight: 1.7, marginBottom: 16 };
    const listStyle = { display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 20, color: 'var(--ink-soft)', fontSize: 15.5, lineHeight: 1.6 };
    const subLabelStyle = { fontWeight: 700, color: 'var(--ink)', marginTop: 16, marginBottom: 8, display: 'block' };

    return (
        <>
            <header>
                <nav className="wrap">
                    <div className="brand">
                        <img src="/assets/QRmor.png" alt="QRmor logo" />
                        <div className="divider" />
                        <img src="/assets/Scinova.png" alt="Scinova Scientifics logo" className="brand-scinova-logo" />
                        <div className="brand-scinova-text">
                            <span className="parent">Scinova Scientifics</span>
                            <span className="brand-tagline"> -Science as a Service</span>
                        </div>
                    </div>
                    <Link to="/" className="btn btn-primary btn-sm">Back to home</Link>
                </nav>
            </header>

            <section>
                <div className="wrap legal-content" style={{ maxWidth: 860, padding: '48px 32px 80px' }}>
                    <span className="eyebrow">QR Technology</span>
                    <h1 style={{ fontSize: 'clamp(28px, 4vw, 28px)', marginTop: 12, marginBottom: 8 }}>
                        Why normal QR codes and online printed QR codes fail?
                    </h1>

                    {/* Section 1 */}
                    <div style={sectionStyle}>
                        <h2 style={h2Style}>Why normal QR codes fail to protect brands from counterfeiting</h2>
                        <ul className="qr-fail-list" style={listStyle}>
                            <li>A normal QR code is just a <b>static picture</b>. It holds fixed information — like a web link or a batch number.</li>
                            <li>It <b>cannot tell the difference</b> between a genuine printed mark and a cloned copy.</li>
                            <li>Anyone can <b>photograph</b> a genuine QR code, reprint it on a fake product, and it will <b>scan normally</b>.</li>
                            <li>The copy shows the <b>same link, same info</b> — nothing looks wrong to the customer.</li>
                        </ul>
                    </div>

                    {/* Section 2 */}
                    <div style={sectionStyle}>
                        <h2 style={h2Style}>Does encryption or online-printed serialized QR codes make them non-clonable?</h2>
                        <p style={introStyle}><b>No.</b></p>
                        <ul className="qr-fail-list" style={listStyle}>
                            <li>Encryption secures only the <b>data behind the code</b> — not the physical printed mark itself.</li>
                            <li>Counterfeiters don't need to break any encryption — a <b>photograph is enough</b> to clone it.</li>
                            <li>A cloned QR code still <b>scans normally and shows genuine results</b>.</li>
                            <li>So encryption or online-printed serialized QR codes <b>cannot protect against counterfeits</b> on their own.</li>
                        </ul>
                    </div>

                    {/* Section 3 - Money wasted */}
                    <div style={sectionStyle}>
                        <h2 style={h2Style}>Money wasted on normal or online-printed QR codes</h2>
                        <p style={introStyle}>
                            Brands spend real money on QR generation, serialization software, and per-unit
                            online printing — but if the printed mark itself can be photographed and
                            reproduced, that spend doesn't buy real counterfeit protection. The cost goes
                            into a system a counterfeiter can defeat with a phone camera.
                        </p>
                    </div>

                    {/* Section 4 - Consequences */}
                    <div style={sectionStyle}>
                        <h2 style={h2Style}>What are the consequences of cloned QR codes?</h2>

                        <span style={subLabelStyle}>For consumers:</span>
                        <ul className="qr-fail-list" style={listStyle}>
                            <li>May unknowingly buy <b>fake, unsafe goods</b> — counterfeit medicine, food, or critical safety equipment.</li>
                            <li>Believe the scan <b>verified authenticity</b>, when it didn't.</li>
                        </ul>

                        <span style={subLabelStyle}>For brands:</span>
                        <ul className="qr-fail-list" style={listStyle}>
                            <li><b>Reputation damage</b>, lost sales, lost customer trust.</li>
                            <li>Possible <b>liability</b> exposure.</li>
                            <li>Counterfeits are usually <b>discovered late</b> — via complaints or returns — since standard QR codes can't flag copies.</li>
                        </ul>
                    </div>

                    {/* Section 5 - How QRmor prevents this */}
                    <div style={sectionStyle}>
                        <h2 style={h2Style}>How does QRmor prevent QR codes from being copied?</h2>
                        <ul className="qr-fail-list" style={listStyle}>
                            <li>QRmor codes are <b>anti-counterfeit, non-clonable, and secure by design</b>.</li>
                            <li>A normal QR code stays identical every time it's photographed and reprinted — a copy is <b>indistinguishable</b> from the original.</li>
                            <li>A QRmor code works differently: its <b>authentication response is generated dynamically</b> at the point of scanning.</li>
                            <li>A photograph or reprint of the visible pattern <b>doesn't reproduce the underlying authentication behavior</b> — that's what makes it non-clonable, not just harder to copy.</li>
                            <li>A fake product carrying a cloned QRmor code gets <b>caught at the point of scanning</b>, not months later.</li>
                            <li>The customer sees a clear <b>"not verified"</b> result instead of being fooled.</li>
                            <li>Every scan is <b>checked and logged securely</b> on the server side, with time and location recorded where available.</li>
                            <li>Instead of just displaying information, QRmor actually checks: <i>is this scan authenticating correctly, or does something look wrong?</i></li>
                            <li>That check protects the customer from buying a fake — and protects the brand's <b>name, safety record, and revenue</b> — because problems are caught at the moment of scanning, not after the damage is done.</li>
                        </ul>
                    </div>
                </div>
            </section>
        </>
    );
}